import { ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import { Grid } from '@breathelife/mui';
import { Localizable } from '@breathelife/types';
import { AutocompleteOption, SimpleCheckbox, Input as TextInput } from '@breathelife/ui-components';
import {
  IconName,
  PdfDocumentType,
  PlatformType,
  QuestionnaireBlueprintCopyableOption,
  QuestionnaireBlueprintCopyableOptions,
  QuestionnaireBlueprintRenderOn,
  SubsectionBlueprint,
} from '@zinnia/form-engine-sdk';

import { SubmitButton } from '../../../../../Components/Button/SubmitButton';
import { dataLabelSelectionToDataLabel } from '../../../../../Helpers/questionnaireEditor/selectOptions';
import { useCarrierContext } from '../../../../../Hooks';
import { ModalLayout } from '../../../../../Layouts/Modal/ModalLayout';
import { AdvancedBlueprintOptions } from '../Components/AdvancedBlueprintOptions';
import { IconNameSelector } from '../Components/IconNameSelector';

export function CreateSubsectionModal(props: {
  onCloseModal: () => void;
  onCreate: (blueprint: SubsectionBlueprint) => void;
  dataLabelOptions: AutocompleteOption[];
}): ReactElement {
  const { onCloseModal, onCreate, dataLabelOptions } = props;
  const { t } = useTranslation();
  const { languageSettings } = useCarrierContext();
  const enabledLanguages = languageSettings.enabledLanguages;
  const [title, setTitle] = useState<Partial<Localizable>>({});
  const [text, setText] = useState<Partial<Localizable>>({});
  const [iconName, setIconName] = useState<IconName>();
  const [showInNavigation, setShowInNavigation] = useState(false);
  const [dataLabel, setDataLabel] = useState<string>(dataLabelOptions?.length ? dataLabelOptions[0].value : '');
  const [renderOn, setRenderOn] = useState<QuestionnaireBlueprintRenderOn[]>([]);
  const [platforms, setPlatforms] = useState<PlatformType[]>([]);
  const [pdfDocuments, setPdfDocuments] = useState<PdfDocumentType[]>([]);
  const [copyable, setCopyable] = useState<QuestionnaireBlueprintCopyableOption>(
    QuestionnaireBlueprintCopyableOptions.none,
  );

  const onSubmit = useCallback(() => {
    const blueprintDataLabel = dataLabelSelectionToDataLabel(dataLabel);

    const subsectionBlueprint: SubsectionBlueprint = {
      id: uuid(),
      partName: `custom-${uuid()}`,
      text,
      title,
      iconName,
      dataLabel: blueprintDataLabel,
      isCustom: true,
      fieldGroups: [],
      showInNavigation,
      copyable,
    };

    if (renderOn.length) subsectionBlueprint.renderOn = renderOn;
    if (platforms.length) subsectionBlueprint.platforms = platforms;
    if (pdfDocuments.length) subsectionBlueprint.pdfDocuments = pdfDocuments;

    onCreate(subsectionBlueprint);
    onCloseModal();
  }, [
    text,
    title,
    iconName,
    showInNavigation,
    dataLabel,
    onCloseModal,
    onCreate,
    renderOn,
    platforms,
    pdfDocuments,
    copyable,
  ]);

  return (
    <ModalLayout
      maxWidth='md'
      isOpen
      closeModal={onCloseModal}
      title={t('admin.questionnaireManagement.input.createSubsection.title')}
      submitButton={
        <SubmitButton onClick={onSubmit} data-testid='questionnaire-editor-subsection-create'>
          {t('cta.save')}
        </SubmitButton>
      }
    >
      <Grid container spacing={2} alignItems='center'>
        {enabledLanguages.map((language) => (
          <Grid item xs={6} key={`createSubsection-title-${language}`}>
            <TextInput
              inputVariant='outlined'
              label={t('admin.questionnaireManagement.input.titleAndLanguage', { language: t(`language.${language}`) })}
              value={title[language] ?? ''}
              onChange={(event) => {
                const value = event.target.value;
                setTitle((previousValue) => ({ ...previousValue, [language]: value }));
              }}
            />
          </Grid>
        ))}
        {enabledLanguages.map((language) => (
          <Grid item xs={12} key={`createSubsection-text-${language}`}>
            <TextInput
              inputVariant='outlined'
              label={t('admin.questionnaireManagement.input.textAndLanguage', { language: t(`language.${language}`) })}
              multiline={true}
              value={text[language] ?? ''}
              onChange={(event) => {
                const value = event.target.value;
                setText((previousValue) => ({ ...previousValue, [language]: value }));
              }}
            />
          </Grid>
        ))}

        <Grid item xs={12}>
          <IconNameSelector
            label={t('admin.questionnaireManagement.input.subsectionIcon')}
            value={iconName}
            onChange={setIconName}
            disabled={false}
          />
        </Grid>
        <Grid item xs={12} key='createSubsection-showInNavigation'>
          <SimpleCheckbox
            id='createSubsection-showInNavigation'
            label={t('admin.questionnaireManagement.input.showInNavigation')}
            checked={showInNavigation}
            onChange={(event) => {
              setShowInNavigation(event.target.checked);
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <AdvancedBlueprintOptions
            tag='subsection'
            platform={{ selected: platforms, onChange: setPlatforms }}
            renderOn={{ selected: renderOn, onChange: setRenderOn }}
            pdfDocument={{ selected: pdfDocuments, onChange: setPdfDocuments }}
            dataLabel={{
              selected: dataLabel,
              options: dataLabelOptions,
              onChange: (value) => {
                if (value !== null) {
                  setDataLabel(value);
                }
              },
            }}
            copyable={{
              selected: copyable,
              onChange: (value) => {
                if (value !== null) {
                  setCopyable(value as QuestionnaireBlueprintCopyableOption);
                }
              },
            }}
            disabled={false}
          />
        </Grid>
      </Grid>
    </ModalLayout>
  );
}
